// src/features/merchantConfig/lib/humanizeLabel.js
//
// Centralised snake_case -> "Title Case With Acronyms" converter for merchant
// config field names. Used everywhere in the UI so we never accidentally show
// the raw wire key to a merchant.
//
// Rules (per the UI prompt §5):
//   1. Map known acronyms first.
//   2. Replace remaining underscores with spaces.
//   3. Title-case the rest, preserving the acronym replacements.

/**
 * Ordered acronym map. Order matters: longer / more-specific patterns first so
 * shorter prefixes don't eat them (e.g. `whats_app` must be replaced before
 * we ever try to title-case `whats`).
 *
 * Each entry replaces an EXACT snake_case fragment (delimited by underscores
 * or string boundaries) with a properly-cased token.
 *
 * @type {Array<{ pattern: string; replacement: string }>}
 */
const ACRONYM_MAP = [
  { pattern: "whats_app", replacement: "WhatsApp" },
  { pattern: "i_v_r", replacement: "IVR" },
  { pattern: "s_m_s", replacement: "SMS" },
  { pattern: "n_d_r", replacement: "NDR" },
  { pattern: "c_o_d", replacement: "COD" },
  { pattern: "u_r_l", replacement: "URL" },
  { pattern: "i_d", replacement: "ID" },
  // Common already-merged acronyms that may appear once underscores are split.
  // These are matched as whole tokens after the underscore split, see below.
];

/**
 * Acronyms that already appear as a single underscore-delimited token (e.g.
 * `sms_template_id` -> tokens are `sms`, `template`, `id`). Title-casing
 * would otherwise turn `sms` into `Sms` — we map these post-split.
 *
 * @type {Record<string, string>}
 */
const POST_SPLIT_ACRONYMS = {
  sms: "SMS",
  ivr: "IVR",
  ndr: "NDR",
  cod: "COD",
  url: "URL",
  uri: "URI",
  id: "ID",
  api: "API",
  pdf: "PDF",
  csv: "CSV",
  xml: "XML",
  json: "JSON",
  utc: "UTC",
  ist: "IST",
  otp: "OTP",
  kyc: "KYC",
  gst: "GST",
  pan: "PAN",
  ifsc: "IFSC",
  upi: "UPI",
  // `whatsapp` collapses to a single token after acronym pre-pass, but if
  // a snake_case key already came through as e.g. `whatsapp_enabled` we
  // want to title-case it as "WhatsApp Enabled" rather than "Whatsapp Enabled".
  whatsapp: "WhatsApp",
};

/**
 * Replace acronym fragments in the raw snake_case string with their
 * canonical cased form, before any further processing.
 *
 * @param {string} input
 * @returns {string}
 */
function applyAcronymMap(input) {
  let result = input;
  for (const { pattern, replacement } of ACRONYM_MAP) {
    // Replace pattern only on word boundaries (start, end, or surrounded by `_`).
    const re = new RegExp(`(^|_)${pattern}(?=_|$)`, "g");
    result = result.replace(re, (_match, leading) => `${leading}${replacement}`);
  }
  return result;
}

/**
 * Title-case a single token, honouring the post-split acronym map and
 * preserving tokens that are already mixed-case (so "WhatsApp" stays as
 * "WhatsApp" and is not re-cased to "Whatsapp").
 *
 * @param {string} token
 * @returns {string}
 */
function titleCaseToken(token) {
  if (!token) return token;
  // Token may have come through with leading capital from the acronym pre-pass.
  // Detect "already mixed case" — any uppercase character past index 0 — and
  // pass it through unchanged.
  if (/[A-Z]/.test(token.slice(1))) return token;

  const lower = token.toLowerCase();
  if (POST_SPLIT_ACRONYMS[lower]) return POST_SPLIT_ACRONYMS[lower];

  // Standard title-case
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Convert a snake_case (or already-mixed) key from a wire payload into a
 * human-readable label.
 *
 * @param {string} key
 * @returns {string}
 *
 * @example
 *   humanizeLabel("default_pickup_address_id")          // "Default Pickup Address ID"
 *   humanizeLabel("whats_app_enabled")                  // "WhatsApp Enabled"
 *   humanizeLabel("i_v_r_timeout_seconds")              // "IVR Timeout Seconds"
 *   humanizeLabel("cod_remittance_frequency_days")      // "COD Remittance Frequency Days"
 *   humanizeLabel("MERCHANT_WEBHOOK_CONFIGURATIONS")    // "Merchant Webhook Configurations"
 */
export function humanizeLabel(key) {
  if (key === null || key === undefined) return "";
  const str = String(key);
  if (!str) return "";

  // Normalize SCREAMING_SNAKE_CASE coming from the catalog (e.g. an enum value)
  // by lower-casing first, then running through the same pipeline.
  const normalized = str.includes("_") ? str.toLowerCase() : str;

  const withAcronyms = applyAcronymMap(normalized);
  const tokens = withAcronyms.split("_").filter(Boolean);
  return tokens.map(titleCaseToken).join(" ");
}
